import { CategoryBreadcrumb } from '@corratech/pylot-breadcrumbs'
import { Cart, CategoryInterface, Maybe } from '@pylot-data/pylotschema'
import { getDeviceType } from './getDeviceType'
import { getOSType } from './getOSType'
import { remove } from 'js-cookie'

type CartUpdatedAction = 'add' | 'remove' | 'empty'

const RUT_FILTER = '11111111-1'

const cartUpdateEvent = (
    action: CartUpdatedAction,
    item: any,
    cart: Cart | undefined,
    sku?: string
): void => {
    if (cart) {
        if (sku) {
            item = cart.items?.find((i) => getItemSku(i) === sku)
        }
        const prodSku = getItemSku(item)
        const attributes = {
            ...mapProductInCart(item, cart),
            ...getCartData(action, prodSku, cart),
            origen: 'web',
            action,
            device: getDeviceType(),
            os: getOSType()
        }

        if (action === 'remove') {
            attributes.item_quantity = attributes.item_quantity! * -1
        }
        window.exponea.track('cart_update', attributes)
    }
}

const cartUpdateOnQuantityChange = (
    item: any,
    cart: Cart,
    quantity: number
): void => {
    if (cart) {
        const prodSku = getItemSku(item)
        const currProd = cart.items?.find((i) => getItemSku(i) === prodSku)
        if (currProd) {
            currProd.quantity += quantity
        }
        const attributes = {
            ...mapProductInCart(item, cart),
            ...getCartData('add', prodSku, cart),
            item_quantity: quantity,
            origen: 'web',
            action: quantity > 0 ? 'add' : 'remove',
            device: getDeviceType(),
            os: getOSType()
        }
        window.exponea.track('cart_update', attributes)
    }
}

const cartUpdateOnEmptyCart = (cart: Cart): void => {
    if (cart) {
        const lastItem = cart.items?.slice(-1)[0]

        const attributes = {
            ...mapProductInCart(lastItem, cart),
            total_price: 0,
            total_quantity: 0,
            items: JSON.stringify([]),
            origen: 'web',
            action: 'empty',
            device: getDeviceType(),
            os: getOSType()
        }
        window.exponea.track('cart_update', attributes)
    }
}

const viewCategoryEvent = (category: any): void => {
    if (category) {
        const attributes = mapCategoryAttributes(category)
        window.exponea?.track('view_category', attributes)
    }
}

const identifyEvent = (attributes: { [key: string]: any }): void => {
    if (attributes) {
        const validatedRut =
            attributes.registered === RUT_FILTER
                ? ' '
                : formatRut(attributes.registered)

        attributes.registered = validatedRut
        window.exponea.identify(attributes)
    }
}

const viewItemEvent = (product: any): void => {
    if (product) {
        const attributes: any = mapProductAttributes(product)
        attributes.item_id = getItemSku(product)
        window.exponea.track('view_item', attributes)
    }
}

const registrationEvent = (attributes: { [key: string]: any }): void => {
    if (attributes) {
        if (attributes.birthday) {
            attributes.birthday = formatBirthday(attributes.birthday)
        } else {
            delete attributes.birthday
        }
        window.exponea.track('registro', attributes)
    }
}

const customerUpdateEvent = (attributes: { [key: string]: any }): void => {
    if (attributes) {
        if (attributes.rut) {
            attributes.rut = formatRut(attributes.rut)
        }
        if (attributes.birthday) {
            attributes.birthday = formatBirthday(attributes.birthday)
        } else {
            delete attributes.birthday
        }
        window.exponea.update(attributes)
    }
}

const loginEvent = (attributes: { [key: string]: any }): void => {
    if (attributes) {
        window.exponea.track('login', attributes)
    }
}

const checkoutStepEvent = (
    name: string,
    attributes: { [key: string]: any }
): void => {
    if (attributes) {
        const attr = {
            ...attributes,
            device: getDeviceType(),
            os: getOSType()
        }
        window.exponea.track(name, attr)
    }
}

const mapProductInCart = (item: any, cart: Cart) => {
    const itemSku = getItemSku(item)
    return {
        ...mapProductAttributes(item.product ? item.product : item),
        item_id: getItemSku(item),
        item_quantity: cart.items?.find((i) => getItemSku(i) === itemSku)
            ?.quantity
    }
}

const getCartData = (
    action: CartUpdatedAction,
    itemSku: string,
    cart: Cart
) => {
    let cartItems = cart.items?.slice() || []
    if (action === 'remove') {
        cartItems = cartItems.filter((i) => getItemSku(i) !== itemSku)
    }

    const items = cartItems.map((item) => {
        return {
            item_id: getItemSku(item),
            quantity: item?.quantity
        }
    })

    return {
        id_cart: cart.id,
        items: JSON.stringify(items.length ? items : []),
        total_quantity: cartTotalQuantity(items),
        total_price: cartTotalPrice(cartItems)
    }
}

const cartTotalQuantity = (items: Array<any>) => {
    return items?.reduce((partialSum, item) => {
        return partialSum + item?.quantity!
    }, 0)
}

const cartTotalPrice = (items: Array<any>) => {
    return items?.reduce((partialSum, item) => {
        // eslint-disable-next-line prettier/prettier
        return partialSum + (item?.prices?.price!.value! * item?.quantity!)
    }, 0)
}

const mapCategoryAttributes = (category: any) => {
    const breadcrumbs: CategoryBreadcrumb[] = category.breadcrumbs || []
    const eventCategories: { [key: string]: any } = {}
    breadcrumbs.forEach((crumb, idx: number) => {
        eventCategories[`cat_espacios_level_${idx + 1}`] = crumb.category_name
    })
    eventCategories[`cat_espacios_level_${breadcrumbs.length + 1}`] =
        category.name

    return {
        origen: 'web',
        ...eventCategories
    }
}

const mapProductAttributes = (product: any) => {
    return {
        item_name: product.name,
        item_tamaño_embalado: product.medidas_de_embalaje_y_peso,
        item_price: product.price_range.minimum_price?.final_price.value,
        item_pais_origen: product.diseno_pais_origen,
        item_color_comercial: mapProductColor(product.configurable_options),
        ...mapProductCategories(product.categories),
        item_espacio_de_la_casa: product.espacio_de_la_casa,
        item_material: product.medidas_y_materialidad
            ?.replaceAll(/<p>|<b>|<\/p>|/g, '')
            ?.replaceAll(/<br>|<\/b>/g, ' ')
    }
}

const mapProductColor = (configurable_options: Array<any>) => {
    if (configurable_options) {
        // eslint-disable-next-line prettier/prettier
        const colorConf = configurable_options.find((option) => option.attribute_code === 'color')
        if (colorConf) {
            return colorConf.values.length > 1
                ? 'Multicolor'
                : colorConf.values[0]?.label
        }
    }
}

const mapProductCategories = (
    categories: Maybe<Array<Maybe<CategoryInterface>>>
): { [key: string]: any } => {
    const filteredCategories =
        categories?.filter((cat) => {
            if (cat?.breadcrumbs) {
                return cat
            }
        }) || []

    filteredCategories?.sort((a, b) => {
        if (a!.breadcrumbs!.length > b!.breadcrumbs!.length) {
            return -1
        }

        if (a!.breadcrumbs!.length < b!.breadcrumbs!.length) {
            return 1
        }

        return 0
    })

    const eventCategories: { [key: string]: any } = {}
    const breadcrumbsLength = filteredCategories[0]?.breadcrumbs?.length || 0
    filteredCategories[0]?.breadcrumbs?.forEach((crumb, idx: number) => {
        eventCategories[`cat_espacios_level_${idx + 1}`] = crumb!.category_name
    })

    eventCategories[`cat_espacios_level_${breadcrumbsLength + 1}`] =
        filteredCategories[0]?.name!

    return eventCategories
}

const getItemSku = (item: any) => {
    if (
        Array.isArray(item.configurable_options) &&
        item.configurable_options[0]?.sku_child
    ) {
        return item.configurable_options[0].sku_child
    }

    return item.product?.sku || item.sku
}

const formatRut = (rut: string) => {
    if (rut) {
        rut = rut.replaceAll('.', '').toLowerCase()
        const pattern = /^(\d+-[\dkK])$/g
        if (!pattern.test(rut)) {
            const splitRut = rut.split('')
            splitRut.splice(-1, 0, '-')
            return splitRut.join('')
        }
        return rut
    }
}

const formatBirthday = (date: string) => {
    if (date) {
        const splitDate = date.split('/')

        return `${new Date().getFullYear()}-${splitDate[1]}-${splitDate[0]}`
    }
}

const showCarouselWebLayer = (cart: Cart): void => {
    if (cart?.items?.length) {
        const sku = cart.items[0]?.product.sku
        window.exponea.showWebLayer(
            '63754fa3ab1f3a89a40ea2a0',
            {
                sku: sku
            },
            true,
            console.log('GA loaded')
        )
    }
}

export {
    cartUpdateEvent,
    checkoutStepEvent,
    identifyEvent,
    registrationEvent,
    viewCategoryEvent,
    viewItemEvent,
    mapProductCategories,
    cartUpdateOnQuantityChange,
    cartUpdateOnEmptyCart,
    customerUpdateEvent,
    loginEvent,
    getItemSku,
    showCarouselWebLayer
}
